import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

function formatCurrency(amount, currency) {
  try {
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: (currency || "eur").toUpperCase(),
    }).format(amount);
  } catch {
    return `€ ${Number(amount).toFixed(2)}`;
  }
}

export default function PayQuote() {
  const { quoteId } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.get(`/quotes/${quoteId}`);
        if (mounted) setQuote(data);
      } catch (err) {
        if (mounted)
          setError(
            err?.response?.data?.detail || "Preventivo non trovato o scaduto"
          );
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [quoteId]);

  const startPayment = async () => {
    setPaying(true);
    try {
      const { data } = await api.post("/checkout/quote", {
        quote_id: quoteId,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("URL di checkout non ricevuto");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Impossibile avviare il pagamento"
      );
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-carbon text-warm flex flex-col">
      <div className="max-w-3xl mx-auto w-full px-6 lg:px-12 py-16 lg:py-24">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs text-[#FAF9F6]/55 hover:text-[#C5A880] transition-colors mb-16"
          data-testid="pay-back-link"
        >
          <ArrowLeft size={14} /> Torna al sito
        </Link>

        <div className="overline mb-4">Preventivo</div>
        <h1 className="font-display text-4xl lg:text-5xl font-medium leading-[1.02] tracking-tight">
          Pagamento parere online
        </h1>

        {loading && (
          <div className="mt-12 flex items-center gap-3 text-[#FAF9F6]/60">
            <Loader2 size={16} className="animate-spin" />
            Caricamento preventivo...
          </div>
        )}

        {!loading && error && (
          <div
            className="mt-12 border border-[#FAF9F6]/10 p-8 text-[#FAF9F6]/70"
            data-testid="pay-error"
          >
            {error}
          </div>
        )}

        {!loading && quote && (
          <div
            className="mt-12 bg-[#111111] border border-[#FAF9F6]/10 p-8 lg:p-12"
            data-testid="pay-quote-card"
          >
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div>
                <div className="overline mb-2">Intestatario</div>
                <div className="font-display text-xl">{quote.client_name}</div>
              </div>
              <div className="text-right">
                <div className="overline mb-2">Riferimento</div>
                <div className="font-mono text-xs text-[#FAF9F6]/60">
                  {quote.id.slice(0, 8)}
                </div>
              </div>
            </div>

            <div className="mt-8 pt-8 border-t border-[#FAF9F6]/10">
              <div className="overline mb-3">Descrizione</div>
              <p className="text-[15px] text-[#FAF9F6]/80 leading-relaxed">
                {quote.description}
              </p>
            </div>

            <div className="mt-10 pt-8 border-t border-[#FAF9F6]/10 flex items-end justify-between gap-6">
              <div>
                <div className="overline mb-2">Importo</div>
                <div
                  className="font-display text-4xl lg:text-5xl font-medium text-[#C5A880]"
                  data-testid="pay-amount"
                >
                  {formatCurrency(quote.amount, quote.currency)}
                </div>
              </div>
              <div className="text-right">
                <div className="overline mb-2">Stato</div>
                <div
                  className={`text-sm font-semibold ${
                    quote.status === "paid"
                      ? "text-emerald-400"
                      : "text-[#FAF9F6]/75"
                  }`}
                  data-testid="pay-status"
                >
                  {quote.status === "paid" ? "Saldato" : "In attesa di pagamento"}
                </div>
              </div>
            </div>

            {quote.status !== "paid" && (
              <button
                onClick={startPayment}
                disabled={paying}
                className="mt-10 w-full btn-primary inline-flex items-center justify-center gap-3 px-8 py-5 text-[12px] font-semibold tracking-[0.22em] uppercase disabled:opacity-60"
                data-testid="pay-button"
              >
                {paying ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Lock size={14} />
                )}
                Paga in sicurezza · Stripe
              </button>
            )}

            <p className="mt-4 text-[11px] text-[#FAF9F6]/40 text-center">
              Pagamento sicuro tramite Stripe. Nessun dato di pagamento è
              trattato dal nostro server.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
